/* Static Aanplantservice page - hedge planting service info
 * Updated: WhatsApp link now uses the main number +31 85 02 33 564
 * Updated: Restyled to Buitn Planten design system (warm neutrals, bosgroen accent, Playfair headings)
 * Updated: Unified layout to match BlogsPage - ContactBar, bg-white, max-w-7xl, jv-primary headings
 * Updated: Content uses full container width, WhatsApp uses 06 number */

import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone, Mail, FileText } from 'lucide-react';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb from '@/components/PageBreadcrumb';

const images = [
  {
    src: '/assets/aanplantservice/aanplantservice-aan-het-werk.webp',
    alt: 'Aanplantservice aan het werk',
  },
  {
    src: '/assets/aanplantservice/aanplantservice-resultaat-voortuin-klein.webp',
    alt: 'Resultaat voortuin klein',
  },
  {
    src: '/assets/aanplantservice/aanplantservice-resultaat-voortuin-klein-zonder-materiaal.webp',
    alt: 'Resultaat voortuin klein zonder materiaal',
  },
  {
    src: '/assets/aanplantservice/aanplantservice-resultaat.webp',
    alt: 'Aanplantservice resultaat',
  },
];

export default function AanplantservicePage() {
  return (
    <>
      <Helmet>
        <title>Aanplantservice | Buitn Planten</title>
        <meta
          name="description"
          content="Naast het leveren van hagen kunnen wij ook het volledige aanplanten voor u verzorgen. Vakkundige plaatsing en een goede start voor uw haag."
        />
        <link rel="canonical" href="https://buitnplanten.nl/klantenservice/aanplantservice" />
      </Helmet>

      <ContactBar />
      <Header />

      <main data-fast-scroll="aanplantservice_viewed" className="pt-8 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Klantenservice', href: '/klantenservice' }, { label: 'Aanplantservice' }]} />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-jv-primary mb-6">
              Aanplanten van hagen
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Naast het leveren van hagen kunnen wij ook het volledige aanplanten voor u verzorgen.
              Wij zorgen voor een vakkundige plaatsing, de juiste plantafstand en een goede start
              voor uw haag. Zo bent u verzekerd van een mooi en gezond resultaat, zonder dat u er
              zelf omkijken naar heeft.
            </p>
            <a
              href="/projectprijs-aanvragen"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-jv-primary px-8 py-4 text-lg font-medium text-white shadow-[0_8px_24px_-8px_rgba(30,43,34,0.4)] transition-all duration-200 hover:bg-jv-accent"
            >
              <FileText className="h-5 w-5" strokeWidth={1.6} />
              Projectprijs aanvragen
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-12 grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            {images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                className="overflow-hidden shadow-[0_8px_24px_-8px_rgba(35,38,35,0.15)] aspect-[4/3]"
                style={{
                  borderRadius:
                    index % 2 === 0
                      ? '5rem 0.5rem 5rem 0.5rem'
                      : '0.5rem 5rem 0.5rem 5rem',
                }}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-2xl border border-[#e7e1d6] bg-[#f2ede4] p-8 text-center"
          >
            <h2 className="mb-4 text-2xl font-bold text-jv-primary">
              Interesse in onze aanplantservice?
            </h2>
            <p className="mb-6 text-gray-600">
              Neem gerust contact met ons op of{' '}
              <a href="/projectprijs-aanvragen" className="font-medium text-jv-primary underline hover:text-jv-accent">
                vraag een projectprijs aan
              </a>
              .
            </p>
            <div className="flex flex-col gap-4 justify-center sm:flex-row">
              <a
                href="tel:+31850233564"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#cfcac2] bg-white px-6 py-3 font-medium text-jv-primary transition-colors hover:border-jv-primary"
              >
                <Phone className="h-5 w-5" strokeWidth={1.6} />
                Bel ons
              </a>
              <a
                href="https://wa.me/31850233564"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-jv-primary px-6 py-3 font-medium text-white transition-colors hover:bg-jv-accent"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                WhatsApp met ons
              </a>
              <a
                href="mailto:info@buitnplanten.nl"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#cfcac2] bg-white px-6 py-3 font-medium text-jv-primary transition-colors hover:border-jv-primary"
              >
                <Mail className="h-5 w-5" strokeWidth={1.6} />
                Mail ons
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
}
